import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, IconCheck, Link, Modal,
  Skeleton,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import snappyMonarxPath from '../../media/snappy-monarx.svg';
import * as Styles from './ActiveProModal.styles';
import { SERVICE_TERMS } from '@/config/urls/supportUrls';
import type { LocaleWithGatorProtectActiveProModal } from '../../gatorProtect.types';
import { getActiveProModalCopy, resolveActiveProButtonLabel } from './getActiveProModalCopy';
import type { ActiveProModalProps } from './ActiveProModal.types';

const DEFAULT_TEST_ID = 'gator-protect-protection-modal';

const ActiveProModal = ({
  open,
  onClose,
  onActivate,
  isTurbo = false,
  monthly,
  loading = false,
  fastCheckoutLoading,
  testId = DEFAULT_TEST_ID,
}: ActiveProModalProps) => {
  const { gatorProtect: gatorProtectLocale } = useLocale() as LocaleWithGatorProtectActiveProModal;
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) {
      setAgreed(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const copy = getActiveProModalCopy(gatorProtectLocale?.activeProModal, isTurbo);

  if (!copy) {
    return null;
  }

  const content = {
    title: copy.title,
    subtitle: copy.subtitle,
    checklistItems: copy.checklistItems,
    buttonLabel: resolveActiveProButtonLabel(copy, monthly),
    productTitle: copy.productTitle,
    productSubtitle: copy.productSubtitle,
    proBadgeLabel: copy.proBadgeLabel,
    mascotAlt: gatorProtectLocale?.banner?.img?.alt ?? '',
  };

  return (

    <Modal
      maxWidth={800}
      maxHeight={662}
      noContentPadding
      noHeader
      onClose={onClose}
      testId={testId}
    >
      <Styles.Container>
        <Styles.Title>{content.title}</Styles.Title>
        <Styles.Subtitle>{loading ? <Skeleton variant="textLine" width="440px" /> : content.subtitle}</Styles.Subtitle>

        <Styles.GradientCardWrapper>
          <Styles.GradientCard>
            <Styles.ProductHeader>
              <Styles.ProductTitleWrapper>
                <Styles.ProductTitle>{content.productTitle}</Styles.ProductTitle>
                {content.proBadgeLabel && (
                  <Styles.ProBadge>{content.proBadgeLabel}</Styles.ProBadge>
                )}
              </Styles.ProductTitleWrapper>
              <Styles.ProductSubtitle>{content.productSubtitle}</Styles.ProductSubtitle>
            </Styles.ProductHeader>

            {(content.checklistItems || []).length > 0 && (
              <Styles.Checklist data-testid={`${testId}__checklist`}>
                {(content.checklistItems || []).map(item => (
                  <Styles.ChecklistItem key={item}>
                    <Styles.ChecklistIcon>
                      <IconCheck size="md" />
                    </Styles.ChecklistIcon>
                    {item}
                  </Styles.ChecklistItem>
                ))}
              </Styles.Checklist>
            )}

            <Styles.CheckboxWrapper
              onClick={() => setAgreed(v => !v)}
              aria-labelledby="active-pro-modal__checkbox-label"
              role="checkbox"
              aria-checked={agreed}
            >
              <Checkbox
                checked={agreed}
              />

              <Styles.TermsLabel id="active-pro-modal__checkbox-label">
                {copy.termsPrefix}
                <Link href={SERVICE_TERMS} text={copy.termsLink ?? ''} />
              </Styles.TermsLabel>
            </Styles.CheckboxWrapper>

            <Styles.ButtonWrapper>
              {loading ? (
                <Skeleton variant="button" />
              )
                : (
                  <Button
                    label={content.buttonLabel}
                    onClick={() => onActivate('monthly')}
                    size="xlarge"
                    variant="primary"
                    version="v2"
                    width="fit"
                    loading={fastCheckoutLoading}
                    disabled={!agreed || fastCheckoutLoading}
                    testId={`${testId}__cta-activate`}
                  />
                )
              }
            </Styles.ButtonWrapper>
          </Styles.GradientCard>

          <Styles.Mascot src={snappyMonarxPath} alt={content.mascotAlt} />
        </Styles.GradientCardWrapper>
      </Styles.Container>
    </Modal>
  );
};

export type { ActiveProModalProps, ActiveProModalLocale } from './ActiveProModal.types';
export type { ActiveProModalResolvedCopy } from './getActiveProModalCopy';

export default withErrorBoundary(ActiveProModal, 'ActiveProModal');
